/**
 * The languages Kalqa speaks, and how one is chosen.
 *
 * MCK-20260521-010 makes multilingual a hard requirement rather than a later phase: the
 * buyer is a Slovak architect coordinating with Czech engineers and German clients, and a
 * sign-in screen that only speaks English has already lost the room. So the language switch
 * exists on the very first screen, before there is a session to hang a preference on —
 * which is why the pre-auth choice lives in localStorage and not in the profile.
 *
 * **Once somebody is signed in, the studio decides** (owner, 2026-09-12: *"jazyk si musí
 * zvoliť štúdio, aj do databázy, ale môže mať default podľa jazyka prehliadača; potom si to
 * vie zmeniť v settings"*). The browser is the **default at creation** — B0 pre-selects from
 * it so a Slovak studio is not asked to find *slovenčina* in a list — and from the moment the
 * founder confirms it, `workspace.locale` is the answer. `effectiveLocale` below is that
 * sentence, and it is the only place it is written down.
 */

/** BCP 47 tags. `cs` is Czech — "CZ" is the flag/label, never the code. */
export const SUPPORTED_LOCALES = ['sk', 'cs', 'de', 'en'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/**
 * English is the fallback because it is the only catalog that is complete. It is not the
 * expected language of the buyer, and nothing in the code should assume it is.
 */
export const FALLBACK_LOCALE: Locale = 'en';

/** What the switcher shows. Deliberately the country-ish label, not the BCP 47 tag. */
export const LOCALE_LABELS: Readonly<Record<Locale, string>> = {
  sk: 'SK',
  cs: 'CZ',
  de: 'DE',
  en: 'EN',
};

export const LOCALE_STORAGE_KEY = 'kalqa.locale';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * The browser's preference, narrowed to something we can actually render — or `null` when it
 * names nothing we have.
 *
 * Matching is on the primary subtag only: `de-AT` and `de-CH` are both `de` here, because
 * the catalogs are per language and a regional variant we do not have is not a reason to
 * fall through to English.
 *
 * **`null` rather than English**, which is the whole reason this is separate from
 * `detectLocale`. To a screen with no second source the two are the same answer; to one that
 * has a fallback underneath — the share surface reads the sending project's locale
 * (`KLQ-20260826-002` §F) — *"the browser asked for French"* and *"the browser asked for
 * English"* have to be told apart, because only the first should fall through.
 */
export function matchLocale(tags: readonly string[]): Locale | null {
  for (const tag of tags) {
    const primary = tag.toLowerCase().split('-')[0];
    if (isLocale(primary)) return primary;
  }
  return null;
}

/** The browser's preference, narrowed, with English where it names nothing we have. */
export function detectLocale(languages: readonly string[]): Locale {
  return matchLocale(languages) ?? FALLBACK_LOCALE;
}

/**
 * **The rule: the studio's language, else the reader's own.** One function, because the same
 * sentence is asked by the guard that adopts it and by every test that pins it (retro rule 8).
 *
 * `studioTag` is `workspace.locale` as `GET /auth/me` reports it, and `null` means *nobody has
 * chosen* — a session that is not signed in, or a workspace that has not been through B0. The
 * server suppresses the column before the claim precisely so that "nobody has chosen" and "the
 * configured default" are not the same value on the wire (`WorkspaceMembershipSummary.locale`).
 *
 * **A studio tag this bundle cannot render falls to English, never to the browser.** A studio
 * that picked Magyar gets English, which is the fallback every untranslated message already
 * resolves to — and emphatically not Slovak because the reader happens to sit at a Slovak
 * laptop. Handing the browser the decision after the studio has made one is the exact failure
 * the 2026-09-12 reversal exists to end: a firm's screens must read the same to everybody in
 * it, wherever they open them.
 *
 * TWIN: `preferred_mail_locale` in `backend/src/kalqa/jobs/email_templates.py` (retro rule 12)
 * — the mails' version of the same rule: the studio's, else the language of the screen that
 * asked, else English. The server may not read the browser's header, so a screen that starts
 * a mail (sign-up, a sign-in link request) sends its own rendered language as `locale` in the
 * body; a change to the order here changes there too.
 */
export function effectiveLocale(studioTag: string | null, readersOwn: Locale): Locale {
  if (studioTag === null || studioTag === '') return readersOwn;
  return matchLocale([studioTag]) ?? FALLBACK_LOCALE;
}

/** What the browser says it wants, in the shape `matchLocale` reads. */
export function browserLanguages(): readonly string[] {
  return navigator.languages.length > 0 ? navigator.languages : [navigator.language];
}

/** The persisted pre-auth choice, or `null` when nobody has made one. */
export function storedLocale(): Locale | null {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Private-mode Safari and locked-down enterprise profiles throw on localStorage access.
    // A language preference is not worth a blank screen; fall through to detection.
  }
  return null;
}

/** Reads the persisted pre-auth choice, falling back to the browser's own preference. */
export function readStoredLocale(): Locale {
  return storedLocale() ?? detectLocale(browserLanguages());
}

export function writeStoredLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // See above — the choice still applies for this page load, it just will not survive it.
  }
}

/**
 * How a short date is drawn, as the reader chose it in this browser (`MCK-20260521-010`,
 * owner 2026-09-15). **Not a language**: it never feeds `effectiveLocale`, the studio does not
 * decide it, and no server column holds it.
 *
 * - `auto` — whatever `formats.ts`'s `NUMERIC_DATE_LOCALES` says for the rendered language:
 *   `20.08.2026` in `sk`/`cs`/`de`, `20 Aug 2026` in `en`.
 * - `numeric` — the day-month-year digits in every language.
 * - `medium` — the abbreviated month name in every language.
 */
export type DateFormatChoice = 'auto' | 'numeric' | 'medium';

export const DATE_FORMAT_CHOICES: readonly DateFormatChoice[] = ['auto', 'numeric', 'medium'];

export const DATE_FORMAT_STORAGE_KEY = 'kalqa.dateFormat';

export function isDateFormatChoice(value: unknown): value is DateFormatChoice {
  return (
    typeof value === 'string' && (DATE_FORMAT_CHOICES as readonly string[]).includes(value)
  );
}

/** The persisted date-shape choice, or `null` when nobody has made one. */
export function storedDateFormat(): DateFormatChoice | null {
  try {
    const stored = window.localStorage.getItem(DATE_FORMAT_STORAGE_KEY);
    if (isDateFormatChoice(stored)) return stored;
  } catch {
    // Same as `storedLocale`: a blocked `localStorage` is not worth a blank screen.
  }
  return null;
}

/** Reads the persisted date-shape choice, `auto` when there is none. */
export function readStoredDateFormat(): DateFormatChoice {
  return storedDateFormat() ?? 'auto';
}

export function writeStoredDateFormat(choice: DateFormatChoice): void {
  try {
    window.localStorage.setItem(DATE_FORMAT_STORAGE_KEY, choice);
  } catch {
    // See `writeStoredLocale` — it applies for this page load and does not survive it.
  }
}
